/**
 * ChallengeMode — Structured survival challenges.
 *
 * NOT IN MINECRAFT.
 *
 * Players can start a challenge run:
 * - Day 1: Get wood + stone + food
 * - Day 3: Build shelter + sleep at night
 * - Day 5: Get iron tools
 * - Day 7: First zombie night (hard)
 * - Day 10: Find diamond
 * - Day 15: Make armor
 * - Day 20: Visit Nether
 * - Day 30: Defeat Wither
 * - Day 40: Find Stronghold
 * - Day 50: Defeat Ender Dragon
 *
 * Tracks completion time, deaths, shows leaderboard-ready stats.
 * Awards legendary achievements on completion.
 */

const CHALLENGES = [
  { day: 1,  id: 'c1',  title: 'Primeros Pasos',        desc: 'Consigue 10 bloques de madera y 5 de piedra' },
  { day: 3,  id: 'c3',  title: 'Refugio Nocturno',       desc: 'Construye un refugio y duerme una noche' },
  { day: 5,  id: 'c5',  title: 'Edad de Hierro',         desc: 'Forja herramientas de hierro completas' },
  { day: 7,  id: 'c7',  title: 'La Primera Horda',       desc: 'Sobrevive una noche con 10+ mobs hostiles' },
  { day: 10, id: 'c10', title: 'Brillo Azul',            desc: 'Mina tu primer diamante' },
  { day: 15, id: 'c15', title: 'Caballero',              desc: 'Fabrica armadura de hierro completa' },
  { day: 20, id: 'c20', title: 'Al Infierno',            desc: 'Entra al Nether' },
  { day: 25, id: 'c25', title: 'Maestro Encantador',     desc: 'Encanta un item con nivel 30' },
  { day: 30, id: 'c30', title: 'Cazador de Jefes',       desc: 'Derrota al Wither' },
  { day: 40, id: 'c40', title: 'Arqueólogo',             desc: 'Encuentra el Stronghold' },
  { day: 50, id: 'c50', title: 'El Vencedor',            desc: 'Derrota al Ender Dragon' },
];

const STORAGE_KEY = 'fedeworld_challenge';

export class ChallengeMode {
  constructor() {
    this.active = false;
    this.currentDay = 1;
    this.startTime = 0;
    this.deaths = 0;
    this.completedChallenges = new Set();
    this.load();

    this.onChallengeComplete = null;
    this.onAllComplete = null;

    this._buildUI();
  }

  load() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      this.active = saved.active || false;
      this.currentDay = saved.day || 1;
      this.startTime = saved.startTime || 0;
      this.deaths = saved.deaths || 0;
      this.completedChallenges = new Set(saved.completed || []);
    } catch {}
  }

  save() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      active: this.active,
      day: this.currentDay,
      startTime: this.startTime,
      deaths: this.deaths,
      completed: Array.from(this.completedChallenges),
    }));
  }

  start() {
    this.active = true;
    this.currentDay = 1;
    this.startTime = Date.now();
    this.deaths = 0;
    this.completedChallenges.clear();
    this.save();
    this.show();
  }

  stop() {
    this.active = false;
    this.save();
    this._overlay.style.display = 'none';
    if (this._hudPanel) this._hudPanel.style.display = 'none';
  }

  onDayChange(newDay) {
    if (!this.active) return;
    this.currentDay = newDay;
    // Check for expiration of challenges
    for (const c of CHALLENGES) {
      if (c.day === newDay && !this.completedChallenges.has(c.id)) {
        // Arrived at day, show notification
        if (this.onChallengeUnlocked) {
          this.onChallengeUnlocked(c);
        }
      }
    }
    this.save();
    this._updateHUD();
  }

  onDeath() {
    if (!this.active) return;
    this.deaths++;
    this.save();
  }

  /** Manually mark a challenge as complete (called from game events). */
  markComplete(id) {
    if (!this.active) return;
    if (this.completedChallenges.has(id)) return;
    this.completedChallenges.add(id);
    this.save();

    const challenge = CHALLENGES.find(c => c.id === id);
    if (this.onChallengeComplete) {
      this.onChallengeComplete(challenge);
    }

    // Check all complete
    if (this.completedChallenges.size === CHALLENGES.length && this.onAllComplete) {
      const elapsedHours = (Date.now() - this.startTime) / 3600000;
      this.onAllComplete({
        timeHours: elapsedHours,
        deaths: this.deaths,
      });
    }
    this._updateHUD();
  }

  _buildUI() {
    // HUD mini-panel (top-right) — shows current next challenge
    this._hudPanel = document.createElement('div');
    this._hudPanel.style.cssText = `
      position: fixed; top: 130px; right: 16px; width: 240px;
      background: rgba(0,0,0,0.6); border-left: 4px solid #FF6F00;
      padding: 8px 12px; font-family: 'Courier New', monospace;
      color: #FFF; font-size: 12px; z-index: 150; display: none;
    `;
    document.body.appendChild(this._hudPanel);

    // Full overlay panel
    this._overlay = document.createElement('div');
    this._overlay.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(0,0,0,0.8); z-index: 919; display: none;
      justify-content: center; align-items: center;
    `;

    const panel = document.createElement('div');
    panel.style.cssText = `
      background: #2A2A2A; border: 4px solid #FF6F00;
      padding: 24px; min-width: 500px; max-height: 80vh; overflow-y: auto;
      font-family: 'Courier New', monospace; color: #DDD;
    `;

    const title = document.createElement('div');
    title.innerHTML = '🏆 Modo Desafío — 50 Días de Supervivencia';
    title.style.cssText = 'font-size:20px;font-weight:bold;color:#FF6F00;margin-bottom:16px;text-align:center;';
    panel.appendChild(title);

    this._statsEl = document.createElement('div');
    this._statsEl.style.cssText = 'font-size:12px;color:#AAA;margin-bottom:12px;text-align:center;';
    panel.appendChild(this._statsEl);

    this._listEl = document.createElement('div');
    this._listEl.style.cssText = 'display:flex;flex-direction:column;gap:6px;';
    panel.appendChild(this._listEl);

    // Buttons
    const btns = document.createElement('div');
    btns.style.cssText = 'display:flex;gap:8px;margin-top:16px;';

    this._startBtn = document.createElement('div');
    this._startBtn.textContent = 'Iniciar Desafío';
    this._startBtn.style.cssText = `
      flex:1; padding:10px; text-align:center; cursor:pointer;
      background:#4CAF50; border:2px solid; border-color:#6ACE70 #2A8A32 #2A8A32 #6ACE70;
      font-weight:bold;
    `;
    this._startBtn.addEventListener('click', () => { this.start(); this._render(); });
    btns.appendChild(this._startBtn);

    this._stopBtn = document.createElement('div');
    this._stopBtn.textContent = 'Salir';
    this._stopBtn.style.cssText = `
      flex:1; padding:10px; text-align:center; cursor:pointer;
      background:#555; border:2px solid; border-color:#777 #333 #333 #777;
      font-weight:bold;
    `;
    this._stopBtn.addEventListener('click', () => this.hide());
    btns.appendChild(this._stopBtn);

    panel.appendChild(btns);
    this._overlay.appendChild(panel);
    document.body.appendChild(this._overlay);
  }

  _render() {
    const elapsed = this.active ? (Date.now() - this.startTime) / 3600000 : 0;
    const h = Math.floor(elapsed);
    const m = Math.floor((elapsed - h) * 60);
    this._statsEl.innerHTML = `
      Día ${this.currentDay} · ${this.completedChallenges.size}/${CHALLENGES.length} completos ·
      ${h}h ${m}m · ${this.deaths} muertes
    `;

    this._listEl.innerHTML = '';
    for (const c of CHALLENGES) {
      const done = this.completedChallenges.has(c.id);
      const unlocked = this.currentDay >= c.day;
      const row = document.createElement('div');
      row.style.cssText = `
        padding:8px 12px;
        background: ${done ? '#2D4A2D' : unlocked ? '#3A3A3A' : '#1A1A1A'};
        border-left: 3px solid ${done ? '#4CAF50' : unlocked ? '#FF6F00' : '#555'};
        opacity: ${unlocked ? 1 : 0.5};
      `;
      row.innerHTML = `
        <div style="font-weight:bold;color:${done ? '#4CAF50' : '#FFF'};">
          ${done ? '✓' : `Día ${c.day}`} · ${c.title}
        </div>
        <div style="font-size:11px;color:#AAA;">${c.desc}</div>
      `;
      this._listEl.appendChild(row);
    }

    this._startBtn.style.display = this.active ? 'none' : 'block';
  }

  _updateHUD() {
    if (!this.active) {
      this._hudPanel.style.display = 'none';
      return;
    }
    // Find next pending challenge
    const next = CHALLENGES.find(c => !this.completedChallenges.has(c.id));
    if (next) {
      this._hudPanel.style.display = 'block';
      this._hudPanel.innerHTML = `
        <div style="color:#FF6F00;font-weight:bold;margin-bottom:4px;">
          🏆 Desafío (Día ${this.currentDay})
        </div>
        <div style="color:#FFF;font-size:12px;">${next.title}</div>
        <div style="color:#AAA;font-size:10px;margin-top:2px;">${next.desc}</div>
        <div style="color:#888;font-size:10px;margin-top:4px;">
          ${this.completedChallenges.size}/${CHALLENGES.length} completos
        </div>
      `;
    }
  }

  show() {
    this._render();
    this._overlay.style.display = 'flex';
    this.isOpen = true;
  }

  hide() {
    this._overlay.style.display = 'none';
    this.isOpen = false;
    if (this.active) this._updateHUD();
  }
}
